/**
 * NOTE EDIT FORM BODY — Form di modifica inline dei campi di una Note.
 *
 * Mirror semplificato di TaskEditFormBody: schema Note ha solo `title` +
 * `description`, niente enum/date/ricorrenza/queue/assignedTo. + picker
 * "Associa ai record" (binder edit mode + AssociatedRecordsField shared).
 *
 * Pattern di edit:
 *  - Doppio click per entrare in edit, blur/Enter per committare.
 *  - Salvataggio via dispatch(updateNote({...})).
 *
 * Il binder in edit mode lavora su scope locale (no slice globale): è
 * sicuro montare il body sia standalone sia embed in ActivityTimeline
 * senza interferire con le relationships del parent Detail.
 */
import { useTranslation } from 'react-i18next';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { RichTextField } from '../../components/RichTextField';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateNote } from './noteThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import type { Note, NoteCreatePayload, NoteUpdatePayload } from './noteTypes';

interface NoteEditFormBodyProps {
  note: Note;
}

export function NoteEditFormBody({ note }: NoteEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();

  const noteFqn = getEntityFqn('Note');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: noteFqn ?? '',
    entityId: note.noteId,
  });

  const commitField =
    (field: keyof NoteCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { noteId: note.noteId, [field]: newValue } as NoteUpdatePayload;
        await dispatch(updateNote(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  return (
    <div className="note-edit-body">
      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('note.title')}</div>
        <EditableField
          value={note.title}
          variant={{ kind: 'text' }}
          required
          onCommit={commitField('title')}
          label={t('note.title')}
        />
      </div>
      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('note.description')}</div>
        <RichTextField
          value={note.description}
          onCommit={commitField('description')}
          placeholder={t('note.descriptionPlaceholder')}
          required
        />
      </div>
      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={noteFqn ?? ''}
        entityId={note.noteId}
      />
    </div>
  );
}
